import { Link, useRouterState } from '@tanstack/react-router'
import { History, LayoutDashboard, PlusCircle } from 'lucide-react'
import { type ComponentType } from 'react'

/**
 * Bottom navigation bar — three primary destinations of the
 * driver app.
 *
 * Three thumb-friendly slots (each 33% width × 56px height) so
 * even one-handed phone use lands the tap. The active route is
 * highlighted with the brand colour; inactive ones stay muted.
 *
 * Routes:
 *   - `/dashboard`        — list of plannings (default landing).
 *   - `/missions/new`     — quick action to start a free mission.
 *   - `/missions/history` — past missions filed by this driver.
 */

interface NavItem {
  to: string
  label: string
  icon: ComponentType<{ size?: number; className?: string }>
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', label: 'Plannings', icon: LayoutDashboard },
  { to: '/missions/new', label: 'Nouveau bon', icon: PlusCircle },
  { to: '/missions/history', label: 'Historique', icon: History },
]

export function BottomNav() {
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname

  return (
    <nav
      aria-label="Navigation principale"
      className={[
        'fixed bottom-0 inset-x-0 z-10',
        'h-16 bg-surface border-t border-surface-border',
        'flex items-stretch',
        // Respect iOS home indicator area — the global `body`
        // padding handles the Android case, but iOS is happier
        // with an explicit cushion on the nav itself.
        'pb-[env(safe-area-inset-bottom)]',
      ].join(' ')}
    >
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon
        // Active when the current path starts with the nav target —
        // covers nested routes like `/missions/new?planningId=...`.
        const active =
          currentPath === item.to || currentPath.startsWith(`${item.to}/`) ||
          (item.to !== '/dashboard' && currentPath.startsWith(item.to))
        return (
          <Link
            key={item.to}
            to={item.to}
            className={[
              'flex-1 flex flex-col items-center justify-center gap-1',
              'min-h-[3rem]',
              'transition-colors duration-100',
              active ? 'text-brand' : 'text-text-muted',
              'active:bg-surface-muted',
            ].join(' ')}
          >
            <Icon size={22} aria-hidden="true" />
            <span className="text-xs font-medium">{item.label}</span>
          </Link>
        )
      })}
    </nav>
  )
}
